import { ReactNode } from "react";
import {
  Box,
  CircularProgress,
  Grid,
  Overlay,
  Stack,
  Tooltip,
  Typography,
  styled,
} from "@financeable-com-au/financeable-ui";

export interface EntityServiceCardLogo {
  src: string;
  alt: string;
  /** In px. */
  height: number;
}

export interface EntityServiceCardProps {
  id: string;
  logos: EntityServiceCardLogo[];
  title: string;
  subtitle: string;
  loadingSubtitle: string;
  isLoading?: boolean;
  isDisabled?: boolean;
  /** Shown on hover, e.g. why the service cannot be used right now. */
  tooltip?: ReactNode;
  onClick: () => void;
}

/** Out of 12. A card never grows past half the row, so a lone card stays the same size as a pair. */
const CARD_COLUMNS = { xs: 12, sm: 6 };

/**
 * `aria-disabled` rather than `disabled`: a natively disabled button gets no hover or focus, so a tooltip explaining
 * why it is unavailable would never open. Everything inside is a `span`, since a button only holds phrasing content.
 */
const CardButton = styled("button")(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(3),
  width: "100%",
  height: "100%",
  padding: theme.spacing(3, 4),
  font: "inherit",
  textAlign: "left",
  color: theme.palette.text.primary,
  backgroundColor: theme.palette.background.surface,
  border: `1px solid ${theme.palette.neutral.outlinedBorder}`,
  borderRadius: theme.radius.md,
  boxShadow: theme.shadow.xs,
  cursor: "pointer",
  transition: "border-color 150ms ease, background-color 150ms ease",
  '&:hover:not([aria-disabled="true"])': {
    borderColor: theme.palette.primary.outlinedBorder,
    backgroundColor: theme.palette.primary.outlinedHoverBg,
  },
  [theme.focus.selector]: theme.focus.default,
  '&[aria-disabled="true"]': {
    cursor: "not-allowed",
    backgroundColor: theme.palette.background.level1,
    borderColor: theme.palette.neutral.outlinedDisabledBorder,
  },
  // A loading card is covered by its overlay instead.
  '&[aria-disabled="true"]:not([aria-busy="true"]) > *': {
    opacity: 0.5,
  },
}));

/** Wide enough for the widest logo, so the titles line up from card to card. */
const LogoSlot = styled("span")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: theme.spacing(1),
  width: 120,
  flexShrink: 0,
}));

/** A compact, clickable card for one of an entity's paid checks, such as an ASIC extract or a credit report. */
const EntityServiceCard = ({
  logos,
  title,
  subtitle,
  loadingSubtitle,
  isLoading = false,
  isDisabled = false,
  tooltip,
  onClick,
}: Omit<EntityServiceCardProps, "id">) => {
  const isInactive = isDisabled || isLoading;

  // `describeChild`: the tooltip adds to the card's name rather than replacing it.
  return (
    <Tooltip title={tooltip} describeChild>
      <CardButton
        type="button"
        aria-disabled={isInactive}
        aria-busy={isLoading}
        onClick={isInactive ? undefined : onClick}
      >
        <LogoSlot>
          {logos.map((logo) => (
            <img key={logo.src} src={logo.src} alt={logo.alt} height={logo.height} />
          ))}
        </LogoSlot>
        <Stack component="span" sx={{ flex: 1, minWidth: 0 }}>
          <Typography component="span" level="title-sm" bold>
            {title}
          </Typography>
          <Typography component="span" level="body-xs" textColor="text.tertiary">
            {subtitle}
          </Typography>
        </Stack>
        {isLoading && (
          <Overlay component="span">
            <Stack
              component="span"
              direction="row"
              sx={{ height: "100%", alignItems: "center", justifyContent: "center", gap: 2 }}
            >
              <CircularProgress size="sm" />
              <Typography component="span" level="body-sm" bold>
                {loadingSubtitle}
              </Typography>
            </Stack>
          </Overlay>
        )}
      </CardButton>
    </Tooltip>
  );
};

export interface EntityServiceCardGroupProps {
  /** Only the cards to show. An empty list renders nothing. */
  cards: EntityServiceCardProps[];
}

/** One provider's cards as a row of half-width cards. */
const EntityServiceCardGroup = ({ cards }: EntityServiceCardGroupProps) => {
  if (cards.length === 0) return null;

  // `2` is 8px: the financeable-ui spacing unit is 4px.
  return (
    <Box sx={{ mt: 2 }}>
      <Grid container spacing={2}>
        {cards.map(({ id, ...card }) => (
          <Grid key={id} {...CARD_COLUMNS}>
            <EntityServiceCard {...card} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export { EntityServiceCard, EntityServiceCardGroup };
